//import SecondaryButton from 'components/common/SecondaryButton';
import React from 'react';
import styles from 'styles/components/AccountDetails.module.sass';
import { Link} from 'react-router-dom';
import Invoices from './Invoices';
import { useAuth } from 'hooks/AuthContext';
import PaymentMethods from './PaymentMethods';
import { usePlanStatus } from 'provider/PlanStatusProvider';

interface AccountDetailsProps {
  onCancelSubscription: () => void;
}

/**
 * AccountDetails component - Displays user account details including subscription, payment methods, invoices, and support contact.
 * 
 * @component
 * @param {AccountDetailsProps} props The component props.
 * @param {() => void} props.onCancelSubscription Callback function to call when the user wants to cancel their subscription.
 * 
 * @example
 * <AccountDetails onCancelSubscription={() => console.log("Cancel Subscription clicked")} />
 * @returns {JSX.Element} AccountDetails component
 */
const AccountDetails: React.FC<AccountDetailsProps> = ({ onCancelSubscription }) => {
  const { userSubscription } = useAuth()

  const accountTypes = usePlanStatus().activeAccountTypes;
  const isPilot = userSubscription?.name.toLowerCase() === 'pilot'

  const handleCancelSubscription = () => {
    if (onCancelSubscription) {
      onCancelSubscription()
    }
  }

  const formattedPilotEndsAt = userSubscription?.pilotEndsAt
    ? new Date(userSubscription.pilotEndsAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : null


  return (
    <div className={styles['account-details']}>
      {/* Manage Password Section */}

      {/* Subscription Section */}
      {userSubscription && userSubscription.name && userSubscription.description && !userSubscription.isOrgMember && (
        <div className={styles['section']}>
          <h3 className={styles['section-label']}>Subscription</h3>
          <div className={styles['section-content']}>
            <p className={styles['section-subtitle']}>{`${userSubscription?.name.substring(0,1).toUpperCase() + userSubscription?.name.substring(1)} Plan`}</p>
            {userSubscription.name !== 'free' && !isPilot && ( <p className={styles['section-text']}>Next billing date: <span className={styles['next-billing-date']}>{userSubscription.nextBillingDate}</span></p>)}
            <p className={`${styles['section-text']} ${styles['subscription-description']}`}>
              {accountTypes.find(account => account.name === userSubscription.name.toLocaleLowerCase())?.description ?? userSubscription.description}
              {isPilot ? (
                <span>
                  {' '}You will have access until{' '}
                  <span className={styles['next-billing-date']}>{formattedPilotEndsAt ?? 'your Pilot period ends'}</span>
                  , after which your account will be updated to a Free account.
                </span>
              ) : userSubscription.name.toLowerCase() !== 'free' && (
                <span> <Link to="#" onClick={() => handleCancelSubscription()} className={styles['cancel-link']}>Cancel</Link> your subscription at any time.</span>
              )}
            </p>
            </div>
        </div>
      )}

      {/* Payment Details Section */}
      <div className={styles['section']}>
        <h3 className={styles['section-label']}>Payment Details</h3>
        <PaymentMethods />
      </div>

      {/* Invoices Section */}
      <div className={styles['section']}>
        <h3 className={styles['section-label']}>Invoices</h3>
        <Invoices status="paid" />
      </div>

      {/* Contact Support Section */}
      <div className={styles['section']}>
        <h3 className={styles['section-label']}>Contact Support</h3>
        <p className={styles['support-text']}>
          Have any concerns or challenges? {' '}
          <Link to="/contact-us" className={styles['contact-link']}>Contact our support</Link> for help.
        </p>
      </div>
    </div>
  );
};

export default AccountDetails;
